import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
  Button,
  Card,
  Space,
  Typography,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  Switch,
} from "antd";
import { EditOutlined, HomeOutlined, PlusOutlined } from "@ant-design/icons";
import { Spinner } from "reactstrap";
import PackagePermisionForm from "./PackagePermisionForm.js";
import PackagePermisionFormUpdate from "./PackagePermisionFormUpdate.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import moment from "moment-timezone";
import { deletePackage } from "../../store/actions/package.js";
import {
  addPackagePermission,
  deletePackagePermission,
  getPackagePermission,
  updatePackagePermission,
  updatePackagePermissionStatus,
} from "../../store/actions/packagePermission.js";
import { getPackageWithoutPermission } from "../../store/actions/withoutPermissionPackage.js";

const { Title } = Typography;

const PackagePermission = () => {
  document.title = "Package Permission";
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalUpdateVisible, setIsModalUpdateVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const dispatch = useDispatch();
  const packagePermission = useSelector(
    (state) => state.packagePermission.list
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await dispatch(getPackagePermission());
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  const handleCreate = async (values) => {
    const formData = new FormData();
    if (selectedRecord) {
      formData.append("id", selectedRecord.id);
      await dispatch(updatePackagePermission({ ...selectedRecord, ...values }));
      await dispatch(getPackagePermission());
      await dispatch(getPackageWithoutPermission());
    } else {
      await dispatch(addPackagePermission(values));
      await dispatch(getPackageWithoutPermission());
      await dispatch(getPackagePermission());
    }
    setIsModalVisible(false);
    setIsModalUpdateVisible(false)
  };

  const handleDelete = async (id) => {
    await dispatch(deletePackagePermission(id));
    await dispatch(getPackagePermission());
  };

  const handleChangeStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1; // Toggle status
    const updatedData = { id: id, status: newStatus }; // Create an object for update
    await dispatch(updatePackagePermissionStatus(updatedData));
    await dispatch(getPackagePermission());
  };

  const renderCell = (text) => (text ? text : "N/A");
  let columns = [
    // {
    //   title: "Role",
    //   dataIndex: "role",
    //   key: "role",
    //   sorter: stringSorter("role"),
    //   sortDirections: ["ascend", "descend"],
    // },
    {
      title: "Package",
      dataIndex: "package",
      key: "package",
      sorter: stringSorter("package"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "createdu",
      dataIndex: "createdu",
      key: "createdu",
      sorter: stringSorter("createdu"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD HH:mm:ss")
          : "",
      sorter: (a, b) =>
        moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
          ? moment(a.createdAt).unix() - moment(b.createdAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      sorter: stringSorter("createdByName"),
      sortDirections: ["ascend", "descend"],
      render: renderCell,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) =>
        moment(date).isValid()
          ? moment(date).format("YYYY-MM-DD HH:mm:ss")
          : "",
      sorter: (a, b) =>
        moment(a.updatedAt).isValid() && moment(b.updatedAt).isValid()
          ? moment(a.updatedAt).unix() - moment(b.updatedAt).unix()
          : 0,
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Updated By",
      dataIndex: "updatedByName",
      key: "updatedByName",
      sorter: stringSorter("updatedByName"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Switch
          checked={text === 1}
          onChange={() => handleChangeStatus(record.id, text)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => {
              setSelectedRecord(record);
              // setIsModalVisible(true);
              setIsModalUpdateVisible(true);
            }}
          >
            <EditOutlined />
          </Button>
          {/* <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];
  columns = enhanceColumnsWithFilters(columns);

  return (
    <div className="page-content">
      <Breadcrumb style={{ marginBottom: "16px" }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
       <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>Package Permission</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                Manage Package Permission
              </Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedRecord(null);
                  setIsModalVisible(true);
                }}
              >
                Add Package Permission
              </Button>
            </Col>
          </Row>
          {isLoading ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <DataTable
              dataSource={packagePermission.map((item) => ({
                ...item,
                key: item.id,
                status: item.status ? 1 : 0,
              }))}
              columns={columns}
            />
          )}
        </Space>
      </Card>
      <PackagePermisionForm
        visible={isModalVisible}
        onCreate={handleCreate}
        onCancel={() => setIsModalVisible(false)}
        // record={selectedRecord}
      />
      <PackagePermisionFormUpdate
        visible={isModalUpdateVisible}
        onCreate={handleCreate}
        onCancel={() => setIsModalUpdateVisible(false)}
        record={selectedRecord}
      />
    </div>
  );
};

export default PackagePermission;
